import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { BaseImage, Modal } from 'stream-chat-react';
import ImageGallery from 'react-image-gallery';
import classNames from 'classnames';
import map from 'lodash/map';

import DownloadIcon from '@ps-design-system/icons/Download/Download24';
import XIcon from '@ps-design-system/icons/X/X24';
import { Spinner } from '@ps-design-system/components/Spinner';

import { useLoading } from '../../../shared-components/utils/hooks/use-loading';
import CarouselArrows from '../../../shared-components/carousel/CarouselArrows';

const onError = (e) => {
  e.target.alt = '';
};

const renderItem = ({ original, originalAlt, type }) => {
  if ('image' === type) {
    return <BaseImage alt={originalAlt} className="image-gallery-item" onError={onError} src={original}/>;
  }
  if ('video' === type) {
    return <video className="image-gallery-item" src={original} playsInline controls />;
  }
  return null;
};

export const StreamModalGallery = ({ items, index, setIndex, open, setOpen, intl }) => {
  const close = useCallback(() => {
    setOpen(false);
  }, [open]);

  const currentItem = useMemo(() => {
    return items && items[index] ? items[index] : {};
  }, [index]);

  const { fallback, title, image_url, asset_url } = currentItem;

  const currentItemUrl = image_url || asset_url;
  const currentFileName = fallback || title;

  const { asyncFn: download, isLoading: isDownloading } = useLoading(async () => {
    const linkElement = document.createElement('a');
    // Create blob URL with peerspace domain
    const blob = await fetch(currentItemUrl).then(res => res.blob());
    linkElement.href = URL.createObjectURL(blob);
    // "download" attribute allows to download files if domain of the downloadable link and website match
    linkElement.download = currentFileName;
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
  });

  const formattedArray = useMemo(() => {
    return map(items, (item) => {
      if ('image' === item.type) {
        const imageSrc = item.image_url || item.thumb_url || '';
        return {
          original: imageSrc,
          originalAlt: intl.formatMessage({
            id: 'stream.modal_gallery.image_alt',
            defaultMessage: 'User uploaded content'
          }),
          source: imageSrc,
          type: item.type
        };
      }
      if ('video' === item.type) {
        const videoSrc = item.asset_url || '';
        return {
          original: videoSrc,
          source: videoSrc,
          type: item.type
        };
      }
    });
  }, [items]);

  return (
    <div className={classNames('StreamModalGallery', { none: !open })}>
      <Modal open={open} onClose={close}>
        <div className="gallery-header absolute full-width">
          <div className="close-button-container absolute">
            <button onClick={close} className="btn-clear">
              <XIcon />
            </button>
          </div>
          <div className="gallery-file-details absolute">
            <div className="heavier">{currentFileName}</div>
          </div>
          <div className="download-button-container absolute">
            <button
              disabled={isDownloading}
              onClick={download}
              className="btn-clear download-button flex middle heavier"
            >
              {isDownloading
                ? <>
                  <Spinner />
                  <FormattedMessage
                    id="stream.modal_gallery.downloading"
                    defaultMessage="Downloading"
                  />
                </>
                : <>
                  <DownloadIcon />
                  <FormattedMessage
                    id="stream.modal_gallery.download"
                    defaultMessage="Download"
                  />
                </>
              }
            </button>
          </div>
        </div>
        <div className="gallery flex center absolute full-width">
          <ImageGallery
            items={formattedArray}
            renderItem={renderItem}
            showIndex={false}
            showPlayButton={false}
            showThumbnails={false}
            showFullscreenButton={false}
            startIndex={index}
            onSlide={setIndex}
            renderRightNav={(onClick) => <CarouselArrows direction="right" onClick={onClick} />}
            renderLeftNav={(onClick) => <CarouselArrows direction="left" onClick={onClick} />}
          />
        </div>
        {1 < items.length
          && <div className="gallery-index text-xs heavier absolute full-width flex center" style={{ direction: 'ltr' }}>
            <FormattedMessage
              id="stream.modal_gallery.image_index"
              defaultMessage="{index} of {total}"
              values={{
                index: index + 1,
                total: items.length
              }}
            />
          </div>
        }
      </Modal>
    </div>
  );
};

StreamModalGallery.propTypes = {
  items: PropTypes.array,
  index: PropTypes.number,
  setIndex: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  intl: intlShape
};

exports.StreamModalGallery = injectIntl(StreamModalGallery);
